import { useEffect, useState, type JSX } from 'react';
import { Hourglass } from './Hourglass.js';
import { useClockOffset } from '../clock.js';
import './Timer.css';

interface TimerProps {
  /** Unix ms when the current phase ends. `null` renders a static "—" display. */
  phaseEndsAt: number | null;
  /** Total duration of the current phase in ms. Used by Hourglass to compute progress. */
  durationMs: number;
}

const SUB_FIVE_THRESHOLD_SECONDS = 5;

/**
 * Hourglass + count digit, with a sub-5s urgency state that compounds four
 * signals at the threshold: a ±1.75px translateX jitter on the hourglass
 * wrapper, a 200ms jitter cycle (faster than the resting cadence), a digit
 * weight snap from 500 to 600, and a 300ms color tween of both the hourglass
 * and the digit from `--page-ink-deep` / `--page-ink` to `--susdle-oxblood`.
 * The hourglass owns its own rAF loop for sand polygon recompute; this
 * component owns the 1Hz digit tick. They share `phaseEndsAt` but are
 * otherwise independent.
 *
 * The "second"/"seconds" unit label was retired — the hourglass icon already
 * carries the unit semantically, and stripping the label lets the digit read
 * as the sole consequential atom on the timer band.
 *
 * Oxblood at sub-5s: the meeting brief originally kept this screen
 * Oxblood-free on the reasoning that timer-expiry isn't a player-action
 * consequence, but playtest-equivalent review found the motion-only state
 * sat below the perceptual threshold. The Two-Accent Rule (DESIGN.md §2)
 * now extends Oxblood to "timer auto-resolution at sub-5s" — the player's
 * time to act is consequential even if no button-press triggers the
 * resolution. The recolor stays restrained (only the 60-80px timer band,
 * well under the 10% rule) and the weight bump keeps the signal visible
 * for color-blind viewers. `data-urgent` lives on the outer `.susdle-timer`
 * so a single attribute scopes all four rules.
 */
export function Timer({
  phaseEndsAt,
  durationMs,
}: TimerProps): JSX.Element {
  const offset = useClockOffset();
  const [secondsRemaining, setSecondsRemaining] = useState<number>(() => {
    if (phaseEndsAt === null) return Math.ceil(durationMs / 1000);
    return Math.max(0, Math.ceil((phaseEndsAt - (Date.now() + offset)) / 1000));
  });

  useEffect(() => {
    if (phaseEndsAt === null) {
      setSecondsRemaining(Math.ceil(durationMs / 1000));
      return;
    }
    /* Re-sync on prop change; the rAF loop in Hourglass uses the same
       phaseEndsAt so the visuals stay aligned. */
    setSecondsRemaining(Math.max(0, Math.ceil((phaseEndsAt - (Date.now() + offset)) / 1000)));

    let raf = 0;
    const tick = () => {
      const next = Math.max(0, Math.ceil((phaseEndsAt - (Date.now() + offset)) / 1000));
      setSecondsRemaining((prev) => (prev === next ? prev : next));
      if (next > 0) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phaseEndsAt, durationMs, offset]);

  const urgent = secondsRemaining > 0 && secondsRemaining <= SUB_FIVE_THRESHOLD_SECONDS;

  return (
    <div className="susdle-timer" data-urgent={urgent ? 'yes' : 'no'}>
      <span className="susdle-timer__hourglass-wrapper">
        <Hourglass phaseEndsAt={phaseEndsAt} durationMs={durationMs} />
      </span>
      <span className="susdle-timer__text">
        <span className="susdle-timer__digit">{secondsRemaining}</span>
      </span>
    </div>
  );
}
